/**
 * Sales Order domain service.
 *
 * Wraps the trusted transactional RPCs (create/allocate/fulfil/cancel) so the
 * UI never knows table names or how allocation/fulfilment is transacted.
 * Status-machine rules live in `src/lib/sales-orders.ts` (pure) and the backend
 * (authoritative); this layer only preserves the operation contract and
 * normalises errors.
 */

import { callRpc } from "@/lib/api/rpc";

export interface SalesLineInput {
  product_id: string;
  variant_id: string | null;
  qty: number;
  unit_price?: number;
  location_id?: string | null;
}

export interface CreateSalesOrderParams {
  customerId: string;
  locationId: string;
  lines: SalesLineInput[];
  source: string;
  status: string;
  currency: string;
  priority: string;
  customerReference: string | null;
  notes: string | null;
  discount: number;
  tax: number;
  shipping: number;
  idempotencyKey: string;
}

export function createSalesOrder(params: CreateSalesOrderParams): Promise<unknown> {
  return callRpc("create_sales_order", {
    p_customer_id: params.customerId,
    p_location_id: params.locationId,
    p_lines: params.lines,
    p_source: params.source,
    p_status: params.status,
    p_currency: params.currency,
    p_priority: params.priority,
    p_customer_reference: params.customerReference,
    p_notes: params.notes,
    p_discount: params.discount,
    p_tax: params.tax,
    p_shipping: params.shipping,
    p_idempotency_key: params.idempotencyKey,
  });
}

export function allocateSalesOrder(
  soId: string,
  lines: { product_id: string; variant_id: string | null; qty: number }[],
): Promise<void> {
  return callRpc("allocate_sales_order", { p_so_id: soId, p_lines: lines });
}

export function cancelSalesOrder(soId: string): Promise<void> {
  return callRpc("cancel_sales_order", { p_so_id: soId });
}

export interface EditSalesOrderParams {
  soId: string;
  shippingAddress?: Record<string, string> | null;
  billingAddress?: Record<string, string> | null;
  priority?: string | null;
  customerReference?: string | null;
  notes?: string | null;
}

/** Header amendment — `edit_sales_order`. Never writes the customer master. */
export function editSalesOrder(params: EditSalesOrderParams): Promise<void> {
  const rpcParams: Record<string, unknown> = { p_so_id: params.soId };
  if (params.shippingAddress !== undefined) rpcParams.p_shipping_address = params.shippingAddress;
  if (params.billingAddress !== undefined) rpcParams.p_billing_address = params.billingAddress;
  if (params.priority !== undefined) rpcParams.p_priority = params.priority;
  if (params.customerReference !== undefined) rpcParams.p_customer_reference = params.customerReference;
  if (params.notes !== undefined) rpcParams.p_notes = params.notes;
  return callRpc("edit_sales_order", rpcParams);
}

export function editSalesOrderLines(soId: string, lines: Record<string, unknown>[]): Promise<void> {
  return callRpc("edit_sales_order_lines", { p_so_id: soId, p_lines: lines });
}

export function reallocateBackorderedItems(orgId: string): Promise<void> {
  return callRpc("reallocate_backordered_items", { p_org_id: orgId });
}

export interface FulfilLineInput {
  product_id: string;
  variant_id: string | null;
  qty: number;
  location_id?: string | null;
}

export function fulfilSalesOrder(params: {
  soId: string;
  locationId: string | null | undefined;
  lines: FulfilLineInput[];
  idempotencyKey?: string;
}): Promise<void> {
  return callRpc("fulfil_sales_order", {
    p_so_id: params.soId,
    p_location_id: params.locationId,
    p_lines: params.lines,
    p_idempotency_key: params.idempotencyKey ?? undefined,
  });
}